import { useEffect, useState } from 'react';

import { Update } from '@material-ui/icons';
import { Tab, Tabs } from '@mui/material';
import {
  Button,
  Datagrid,
  DatagridProvider,
  FormControlLabel,
  Switch,
  Typography,
} from '@opendoor/mosaic';
import {
  useIndxAggregation,
  useIndxPage,
  useIndxResults,
  useIndxSort,
  useIndxTermsFilter,
} from '@opendoor/tessellation';

import { OdProtosHomeworkData_LineItem_State as LineItemState } from '../../graphql/athena/types/globalTypes';
import {
  ListWorkOrders_homework_listWorkOrders_workOrders_lineItems as LineItem,
  ListWorkOrders_homework_listWorkOrders_workOrders as WorkOrder,
} from '../../graphql/athena/types/ListWorkOrders';
import { useListWorkOrders } from '../../graphql/queries/homework';
import ExportToCSV from './components/ExportToCSV';
import { FIELDS, PROGRESS } from './constants';
import configureFields from './Fields';
import Filters, { AppliedFilters } from './filters';
import WorkOrderProvider from './providers/WorkOrderProvider';

interface ProgressObject {
  numCompletedLineItems: number;
  totalLineItems: number;
}

enum FetchPolicy {
  CACHE_FIRST = 'cache-first',
  NO_CACHE = 'no-cache',
}

const fetchPolicyBasedOnTab = (tab: string): FetchPolicy => {
  switch (tab) {
    case PROGRESS[0].key:
    case PROGRESS[1].key:
    case PROGRESS[2].key:
      return FetchPolicy.NO_CACHE;
    default:
      return FetchPolicy.NO_CACHE;
  }
};

const WorkOrders = () => {
  const [fetchPolicy, setFetchPolicy] = useState<FetchPolicy>(FetchPolicy.NO_CACHE);

  const onTabChange = (tab: string) => {
    setFetchPolicy(fetchPolicyBasedOnTab(tab));
  };

  return (
    <WorkOrderProvider fetchPolicy={fetchPolicy}>
      <WorkOrderWithTabs onTabChange={onTabChange} />
    </WorkOrderProvider>
  );
};

const getCountForTab = (progressAgg: any[], key: string): number =>
  progressAgg.find((agg) => agg.key === key)?.count || 0;

const formatCount = (count: number, loading = false): string =>
  loading ? '' : count > 9999 ? ' (9999+)' : ` (${count.toString()})`;

const completedLineItemStates = [LineItemState.WORK_COMPLETED, LineItemState.WORK_VALIDATED];
const excludedLineItemStates = [
  LineItemState.WORK_CANCELED,
  LineItemState.WORK_INVALIDATED,
  LineItemState.DRAFT,
  LineItemState.INVALID,
  LineItemState.SCOPE_REJECTED,
];

interface WorkOrderWithTabsProps {
  onTabChange: (tab: string) => void;
}

const WorkOrderWithTabs = (props: WorkOrderWithTabsProps) => {
  const { results, size, loading, refetch } = useIndxResults();
  const [page, setPage] = useIndxPage();
  const [tab, setProgressFilter] = useIndxTermsFilter(FIELDS.PROGRESS);

  const [progressAgg] = useIndxAggregation(FIELDS.PROGRESS);
  const [isInALL] = useIndxAggregation(FIELDS.IN_ALL_TAB);

  const [lastLoadedAt, setLastLoadedAt] = useState(new Date());
  const [isFilterVisible, setIsFilterVisible] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  const modifiedResults: any[] = [];
  // Clone results so that it can be modified to include progressObject
  results.forEach((workOrder) => modifiedResults.push(Object.assign({}, workOrder)));

  const getProgressObject = (lineItems: LineItem[]): ProgressObject => {
    const numCompletedLineItems = lineItems.reduce((count, lineItem) => {
      return completedLineItemStates.includes(lineItem.state) ? count + 1 : count;
    }, 0);

    const numExcludedLineItems = lineItems.reduce((count, lineItem) => {
      return excludedLineItemStates.includes(lineItem.state) ? count + 1 : count;
    }, 0);

    const totalLineItems = lineItems.length - numExcludedLineItems;

    return {
      numCompletedLineItems,
      totalLineItems,
    };
  };

  const addProgressObjectsToWorkOrders = (workOrders: WorkOrder[]) => {
    const workOrderIDToLineItemsMap = workOrders.reduce(
      (obj: Record<string, LineItem[]>, workOrder: WorkOrder) => {
        obj[workOrder.id] = workOrder.lineItems!;
        return obj;
      },
      {},
    );

    modifiedResults.forEach((workOrder) => {
      const lineItems = workOrderIDToLineItemsMap[workOrder.id];
      workOrder.progressObject = getProgressObject(lineItems);
    });
  };

  const isInProgressTabNotSelected = () =>
    tabIndex === PROGRESS.length || PROGRESS[tabIndex].key !== 'IN_PROGRESS';

  const workOrderIDs = results.map((workOrder) => workOrder.id);
  const {
    data: workOrders,
    loading: loadingWorkOrders,
    error: errorLoadingWorkOrders,
  } = useListWorkOrders({ variables: { ids: workOrderIDs }, skip: isInProgressTabNotSelected() });

  if (!loadingWorkOrders && !errorLoadingWorkOrders && workOrders) {
    addProgressObjectsToWorkOrders(workOrders);
  }

  const onToggleFilterVisibility = () => {
    setIsFilterVisible((prevState) => !prevState);
  };

  const handleRefresh = () => {
    refetch();
    setLastLoadedAt(new Date());
  };

  useEffect(() => {
    setLastLoadedAt(new Date());
  }, [results]);

  useEffect(() => {
    if (tab?.length === 0) {
      setProgressFilter([PROGRESS[0].key]);
    }
    // eslint-disable-next-line
  }, []);

  const [, addSortScheduledStartAt] = useIndxSort('scheduled_start_at');
  const [, addSortScheduledStopAt] = useIndxSort('scheduled_stop_at');
  const [, addSortDateRequestedAt] = useIndxSort('date_requested_at');

  const tabLinks = PROGRESS.map((progress) => {
    const isActive = (tab || []).length === 1 ? (tab as string[])[0] === progress.key : false;
    const count = isActive ? size || 0 : getCountForTab(progressAgg, progress.key);

    return {
      label: progress.value + `${formatCount(count, loading)}`,
      value: [progress.key],
    };
  });

  tabLinks.push({
    label: `All${formatCount(
      (tab || []).length === PROGRESS.length ? size || 0 : getCountForTab(isInALL, '1'),
      loading,
    )}`,
    value: PROGRESS.map((p) => p.key),
  });

  const handleTabChange = (event: React.SyntheticEvent, newIndex: number) => {
    setTabIndex(newIndex);
    const tab = tabLinks[newIndex].value.length === 1 ? tabLinks[newIndex].value[0] : 'ALL';
    props.onTabChange(tab);
    setProgressFilter(tabLinks[newIndex].value);
  };

  const sortOptions = {
    handleSortScheduledStartAt: () => {
      addSortScheduledStartAt({ reset: true });
    },
    handleSortScheduledStopAt: () => {
      addSortScheduledStopAt({ reset: true });
    },
    handleSortDateRequestedAt: () => {
      addSortDateRequestedAt({ reset: true });
    },
  };

  return (
    <div className="mt-4 h-100% w-100% overflow-x-auto">
      <div className="flex items-start mv-4 mh-2">
        <div className="flex-grow-1">
          <Typography variant="h2" component="h2">
            Work orders
          </Typography>
          <Typography variant="caption" className="neutral90" component="span">
            Last updated{' '}
            {lastLoadedAt.toLocaleTimeString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}{' '}
            <Button
              onClick={handleRefresh}
              style={{ cursor: 'pointer', minWidth: '0px', padding: '0px 4px' }}
              className="b classes.disableRipple"
              size="small"
              disableFocusRipple={true}
              disableRipple={true}
              variant="text"
              color="primary"
            >
              <Update fontSize="small" />
            </Button>
          </Typography>
        </div>
        <ExportToCSV />
      </div>
      <DatagridProvider
        data={modifiedResults}
        fields={configureFields(tab || [], sortOptions)}
        totalRows={size || 0}
        page={page}
        setPage={setPage}
        loading={loading}
        emptyMessage="No work orders found."
      >
        <FormControlLabel
          control={
            <Switch
              checked={isFilterVisible}
              onChange={onToggleFilterVisibility}
              value="enabled"
              color="primary"
            />
          }
          label="Show Filters"
          className="ml-1"
        />
        <div className="flex flex-row">
          {isFilterVisible && <Filters />}
          <div className="flex flex-column flex-grow-1 ml-2 mr-4 overflow-x-auto">
            <AppliedFilters />
            <div className="bg-neutral10 mb-4">
              <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable">
                {tabLinks.map((tabLink, index) => (
                  <Tab
                    label={tabLink.label}
                    value={index}
                    key={tabLink.label}
                    sx={{
                      fontFamily: `'Graphik', sans-serif`,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      letterSpacing: 0,
                      textTransform: 'none',
                    }}
                  />
                ))}
              </Tabs>
            </div>
            <Datagrid />
          </div>
        </div>
      </DatagridProvider>
    </div>
  );
};

export default WorkOrders;
